import React, { useState, useEffect, useRef } from 'react';
import { AlertTriangle, Cpu, CheckCircle, XCircle, Loader, MessageSquare, ArrowRight } from 'lucide-react';

/**
 * StepByStepArticleAnalyzer - A component that allows user-controlled step-by-step 
 * analysis of articles using Claude AI, with each step running as an individual job.
 */
const StepByStepArticleAnalyzer = ({ 
  articleId, 
  articleContent, 
  onAnalysisComplete, 
  existingClaims = [], 
  workflowButtons = null, 
  isWorkflow = false,
  onAnalysisStart = null,
  onAnalysisEnd = null
}) => {
  // Component state
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [previewClaims, setPreviewClaims] = useState([]);
  
  // Manual step-by-step analysis state
  const [currentStage, setCurrentStage] = useState('initial'); // initial, claims, tactics, impact, final, completed
  const [stageLoading, setStageLoading] = useState(false);
  
  // Intermediate results for each stage
  const [claimsResult, setClaimsResult] = useState(null);
  const [tacticsResult, setTacticsResult] = useState(null);
  const [impactResult, setImpactResult] = useState(null);
  
  // Job-based async processing state
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [jobProgress, setJobProgress] = useState(0);
  const pollingIntervalRef = useRef(null);
  
  // Clean up polling interval on component unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);
  
  // Progress calculations
  const getProgressPercent = () => {
    if (jobProgress > 0) {
      return jobProgress;
    }
    
    switch(currentStage) {
      case 'initial': return 0;
      case 'claims': return claimsResult ? 25 : 0;
      case 'tactics': return 50;
      case 'impact': return 75;
      case 'final':
      case 'completed': return 100;
      default: return 0;
    }
  };
  
  // Stage descriptions
  const getStageDescription = (stage) => {
    switch(stage) {
      case 'initial': return "Ready to start analysis";
      case 'waiting': return "Preparing for analysis";
      case 'claims': return "Identifying misleading claims and generating factual rebuttals";
      case 'tactics': return "Identifying rhetorical tactics used in the article";
      case 'impact': return "Analyzing democratic impact and historical parallels";
      case 'finalize':
      case 'final': return "Finalizing analysis and generating scores";
      case 'completed': return "Analysis complete";
      case 'failed': return "Analysis failed";
      default: return "Processing article analysis";
    }
  };
  
  // Function to start job polling
  const startPolling = (newJobId) => {
    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    // Start a new polling interval
    pollingIntervalRef.current = setInterval(() => {
      checkJobStatus(newJobId);
    }, 5000); // Poll every 5 seconds
  };
  
  // Function to check job status
  const checkJobStatus = async (jobIdToCheck) => {
    try {
      const response = await fetch(`/api/articles/job-status?job_id=${jobIdToCheck}`);
      
      const contentType = response.headers.get('content-type') || '';
      
      if (!response.ok) {
        // If it's HTML, create a more informative error
        if (contentType.includes('text/html')) {
          console.error('Received HTML error response on status check');
          return; // Don't throw - just log and continue polling
        }
        
        console.error(`Failed to check job status: ${response.statusText}`);
        return; // Continue polling despite error
      }
      
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Error parsing job status response:', parseError);
        return; // Continue polling despite error
      }
      setJobStatus(data);
      setJobProgress(data.progress || 0);
      
      // Update current stage based on job stage
      if (data.stage && data.stage !== currentStage) {
        setCurrentStage(data.stage);
      }
      
      // Store intermediate results if they're available
      if (data.stage === 'claims' && data.has_claims) {
        fetchStageResults(jobIdToCheck, 'claims');
      } else if (data.stage === 'tactics' && data.has_tactics) {
        fetchStageResults(jobIdToCheck, 'tactics');
      } else if (data.stage === 'impact' && data.has_impact) {
        fetchStageResults(jobIdToCheck, 'impact');
      }
      
      // If the job is done (completed or failed), stop polling
      if (data.status === 'completed' || data.status === 'failed') {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
        
        if (data.status === 'completed') {
          // Handle completed job - process the results
          setStageLoading(false);
          setCurrentStage('completed');
          
          if (data.result) {
            processAnalysisResults(data.result);
          } else {
            // If result is not included in status, fetch it separately
            fetchFinalResults(jobIdToCheck);
          }
        } else if (data.status === 'failed') {
          // Handle failed job
          setStageLoading(false);
          setCurrentStage('failed');
          setError(data.error || 'Analysis failed. Please try again.');
          if (onAnalysisEnd) onAnalysisEnd();
        }
      }
      
    } catch (err) {
      console.error('Error checking job status:', err);
      // Don't stop polling on a single error - it might just be a temporary network issue
    }
  };
  
  // Fetch results for a specific stage
  const fetchStageResults = async (jobId, stage) => {
    try {
      // Endpoint to fetch specific stage results
      const response = await fetch(`/api/articles/job-results?job_id=${jobId}&stage=${stage}`);
      
      if (!response.ok) {
        console.error(`Error fetching ${stage} results:`, response.statusText);
        return;
      }
      
      const data = await response.json();
      
      if (stage === 'claims' && data.claims_result) {
        try {
          const parsedResult = JSON.parse(data.claims_result);
          setClaimsResult(parsedResult);
        } catch (e) {
          console.error('Error parsing claims result:', e);
        }
      } else if (stage === 'tactics' && data.tactics_result) {
        try {
          const parsedResult = JSON.parse(data.tactics_result);
          setTacticsResult(parsedResult);
        } catch (e) {
          console.error('Error parsing tactics result:', e);
        }
      } else if (stage === 'impact' && data.impact_result) {
        try {
          const parsedResult = JSON.parse(data.impact_result);
          setImpactResult(parsedResult);
        } catch (e) {
          console.error('Error parsing impact result:', e);
        }
      }
    } catch (err) {
      console.error(`Error fetching ${stage} results:`, err);
    }
  };
  
  // Fetch final analysis results
  const fetchFinalResults = async (jobId) => {
    try {
      const response = await fetch(`/api/articles/job-results?job_id=${jobId}&final=true`);
      
      if (!response.ok) {
        setError('Failed to fetch final analysis results');
        return;
      }
      
      const data = await response.json();
      
      if (data.final_result) {
        try {
          const parsedResult = JSON.parse(data.final_result);
          processAnalysisResults(parsedResult);
        } catch (e) {
          console.error('Error parsing final result:', e);
          setError('Error processing analysis results');
        }
      } else {
        setError('No final analysis results available');
      }
    } catch (err) {
      console.error('Error fetching final results:', err);
      setError('Failed to fetch analysis results');
    }
  };
  
  // Process the current stage
  const processCurrentStage = async () => {
    if (stageLoading) return; // Prevent multiple simultaneous requests
    
    setStageLoading(true);
    setError(null);
    
    try {
      if (currentStage === 'initial') {
        // Start the claims analysis job
        await startClaimsJob();
      }
      else if (currentStage === 'claims' && claimsResult) {
        // Proceed to tactics analysis
        await startTacticsJob();
      }
      else if (currentStage === 'tactics' && tacticsResult) {
        // Proceed to impact analysis
        await startImpactJob();
      }
      else if (currentStage === 'impact' && impactResult) {
        // Proceed to final analysis
        await startFinalJob();
      }
    } catch (err) {
      console.error(`Error processing ${currentStage} stage:`, err);
      setError(err.message || `Error analyzing ${currentStage} stage`);
      setStageLoading(false);
    }
  };
  
  // Start a new job for claims analysis
  const startClaimsJob = async () => {
    try {
      if (onAnalysisStart) onAnalysisStart();
      
      // Submit the job
      const response = await fetch('/api/articles/analyze-staged', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          article_id: articleId,
          stage: 'claims'
        })
      });
      
      // Check if response is HTML (which would indicate a server error page)
      const contentType = response.headers.get('content-type') || '';
      
      if (!response.ok) {
        // If it's HTML, create a more informative error
        if (contentType.includes('text/html')) {
          const htmlContent = await response.text();
          console.error('Received HTML error response:', htmlContent.substring(0, 200) + '...');
          throw new Error(`Server returned HTML error page (Status: ${response.status}). This likely indicates a server-side issue.`);
        }
        
        // Try to parse as JSON but handle errors
        try {
          const errorData = await response.json();
          throw new Error(errorData.error || `Failed to submit claims analysis job: ${response.statusText}`);
        } catch (parseError) {
          throw new Error(`Failed to submit claims analysis job (Status: ${response.status}): ${response.statusText}`);
        }
      }
      
      // Handle successful response
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Error parsing response as JSON:', parseError);
        throw new Error('Server returned invalid JSON response. Check server logs for details.');
      }
      
      // Store the result directly
      setClaimsResult(data.analysis);
      setCurrentStage('claims');
      setStageLoading(false);
      
    } catch (err) {
      console.error('Error starting claims analysis:', err);
      setError(err.message || 'Failed to analyze claims');
      setStageLoading(false);
      if (onAnalysisEnd) onAnalysisEnd();
    }
  };
  
  // Start tactics analysis
  const startTacticsJob = async () => {
    try {
      // Submit the tactics job
      const response = await fetch('/api/articles/analyze-staged', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          article_id: articleId,
          stage: 'tactics',
          previous_results: {
            claims: claimsResult
          }
        })
      });
      
      // Check if response is HTML (which would indicate a server error page)
      const contentType = response.headers.get('content-type') || '';
      
      if (!response.ok) {
        // If it's HTML, create a more informative error
        if (contentType.includes('text/html')) {
          const htmlContent = await response.text();
          console.error('Received HTML error response:', htmlContent.substring(0, 200) + '...');
          throw new Error(`Server returned HTML error page (Status: ${response.status}). This likely indicates a server-side issue.`);
        }
        
        // Try to parse as JSON but handle errors
        try {
          const errorData = await response.json();
          throw new Error(errorData.error || `Failed to submit tactics analysis job: ${response.statusText}`);
        } catch (parseError) {
          throw new Error(`Failed to submit tactics analysis job (Status: ${response.status}): ${response.statusText}`);
        }
      }
      
      // Handle successful response
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Error parsing response as JSON:', parseError);
        throw new Error('Server returned invalid JSON response. Check server logs for details.');
      }
      
      // Store the result directly
      setTacticsResult(data.analysis);
      setCurrentStage('tactics');
      setStageLoading(false);
      
    } catch (err) {
      console.error('Error analyzing tactics:', err);
      setError(err.message || 'Failed to analyze tactics');
      setStageLoading(false);
    }
  };
  
  // Start impact analysis
  const startImpactJob = async () => {
    try {
      // Submit the impact job
      const response = await fetch('/api/articles/analyze-staged', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          article_id: articleId,
          stage: 'impact',
          previous_results: {
            claims: claimsResult,
            tactics: tacticsResult
          }
        })
      });
      
      // Check if response is HTML (which would indicate a server error page)
      const contentType = response.headers.get('content-type') || '';
      
      if (!response.ok) {
        // If it's HTML, create a more informative error
        if (contentType.includes('text/html')) {
          const htmlContent = await response.text();
          console.error('Received HTML error response:', htmlContent.substring(0, 200) + '...');
          throw new Error(`Server returned HTML error page (Status: ${response.status}). This likely indicates a server-side issue.`);
        }
        
        // Try to parse as JSON but handle errors
        try {
          const errorData = await response.json();
          throw new Error(errorData.error || `Failed to submit impact analysis job: ${response.statusText}`);
        } catch (parseError) {
          throw new Error(`Failed to submit impact analysis job (Status: ${response.status}): ${response.statusText}`);
        }
      }
      
      // Handle successful response
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Error parsing response as JSON:', parseError);
        throw new Error('Server returned invalid JSON response. Check server logs for details.');
      }
      
      // Store the result directly
      setImpactResult(data.analysis);
      setCurrentStage('impact');
      setStageLoading(false);
      
    } catch (err) {
      console.error('Error analyzing impact:', err);
      setError(err.message || 'Failed to analyze democratic impact');
      setStageLoading(false);
    }
  };
  
  // Start final analysis
  const startFinalJob = async () => {
    try {
      // Submit the finalize job
      const response = await fetch('/api/articles/analyze-staged', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          article_id: articleId,
          stage: 'finalize',
          previous_results: {
            claims: claimsResult,
            tactics: tacticsResult,
            impact: impactResult
          }
        })
      });
      
      // Check if response is HTML (which would indicate a server error page)
      const contentType = response.headers.get('content-type') || '';
      
      if (!response.ok) {
        // If it's HTML, create a more informative error
        if (contentType.includes('text/html')) {
          const htmlContent = await response.text();
          console.error('Received HTML error response:', htmlContent.substring(0, 200) + '...');
          throw new Error(`Server returned HTML error page (Status: ${response.status}). This likely indicates a server-side issue.`);
        }
        
        // Try to parse as JSON but handle errors
        try {
          const errorData = await response.json();
          throw new Error(errorData.error || `Failed to submit final analysis job: ${response.statusText}`);
        } catch (parseError) {
          throw new Error(`Failed to submit final analysis job (Status: ${response.status}): ${response.statusText}`);
        }
      }
      
      // Handle successful response
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Error parsing response as JSON:', parseError);
        throw new Error('Server returned invalid JSON response. Check server logs for details.');
      }
      
      // Process the final analysis results
      processAnalysisResults(data.analysis);
      setCurrentStage('completed');
      setStageLoading(false);
      
    } catch (err) {
      console.error('Error finalizing analysis:', err);
      setError(err.message || 'Failed to finalize analysis');
      setStageLoading(false);
    }
  };

  // Process the final analysis results
  const processAnalysisResults = (finalResult) => {
    // Store the full result
    setResult(finalResult);
    
    // Format the claims from the analysis
    if (finalResult.misleading_claims && finalResult.misleading_claims.length > 0) {
      
      const claims = finalResult.misleading_claims.map((claim, index) => {
        // Find matching rhetorical tactics for this claim
        const tactics = finalResult.rhetoric_markers
          ? finalResult.rhetoric_markers
              .filter(marker => {
                // Check if this rhetoric marker mentions or relates to this claim
                return (marker.examples && Array.isArray(marker.examples) && 
                        marker.examples.some(example => 
                          claim.claim_text.includes(example) || example.includes(claim.claim_text)
                        ));
              })
              .map(marker => ({
                id: `tactic_${index}_${marker.technique.toLowerCase().replace(/\\s+/g, '_')}`,
                name: marker.technique,
                note: marker.democratic_threat || marker.purpose || ''
              }))
          : [];
        
        // Find the most relevant pillar for this claim
        const democraticImpact = claim.democratic_impact || 
          (finalResult.pillars && 
           finalResult.pillars.length > 0 ? 
             finalResult.pillars[0].justification : 
             'May impact democratic norms and values.');
        
        return {
          claim_text: claim.claim_text,
          rebuttal_text: claim.reality || '',
          evidence_links: Array.isArray(claim.sources) ? 
            claim.sources.filter(s => s.includes('http')).map(s => s.trim()) : [],
          importance_level: claim.importance_level || (5 - Math.min(index, 4)), // Use provided level or fallback
          democratic_impact: democraticImpact,
          rhetorical_tactics: tactics
        };
      });
      
      setPreviewClaims(claims);
      
      // Notify parent component of completion with claims
      if (onAnalysisComplete) {
        onAnalysisComplete({
          claims,
          analysisData: finalResult
        });
      }
    } 
    // Alternative structure: check for structured_analysis
    else if (finalResult.structured_analysis && 
        finalResult.structured_analysis.misleading_claims && 
        finalResult.structured_analysis.misleading_claims.length > 0) {
      
      const claims = finalResult.structured_analysis.misleading_claims.map((claim, index) => {
        // Logic similar to above, but using structured_analysis paths
        const tactics = finalResult.structured_analysis.rhetoric_markers
          ? finalResult.structured_analysis.rhetoric_markers
              .filter(marker => {
                return (marker.examples && Array.isArray(marker.examples) && 
                        marker.examples.some(example => 
                          claim.claim_text.includes(example) || example.includes(claim.claim_text)
                        ));
              })
              .map(marker => ({
                id: `tactic_${index}_${marker.technique.toLowerCase().replace(/\\s+/g, '_')}`,
                name: marker.technique,
                note: marker.democratic_threat || marker.purpose || ''
              }))
          : [];
        
        const democraticImpact = claim.democratic_impact || 
          (finalResult.structured_analysis.pillars && 
           finalResult.structured_analysis.pillars.length > 0 ? 
             finalResult.structured_analysis.pillars[0].justification : 
             'May impact democratic norms and values.');
        
        return {
          claim_text: claim.claim_text,
          rebuttal_text: claim.reality || '',
          evidence_links: Array.isArray(claim.sources) ? 
            claim.sources.filter(s => s.includes('http')).map(s => s.trim()) : [],
          importance_level: claim.importance_level || (5 - Math.min(index, 4)),
          democratic_impact: democraticImpact,
          rhetorical_tactics: tactics
        };
      });
      
      setPreviewClaims(claims);
      
      if (onAnalysisComplete) {
        onAnalysisComplete({
          claims,
          analysisData: finalResult
        });
      }
    } else if (onAnalysisEnd) {
      onAnalysisEnd();
    }
  };
  
  // Reset the analysis
  const resetAnalysis = () => {
    // Clear polling interval if active
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    setCurrentStage('initial');
    setStageLoading(false);
    setClaimsResult(null);
    setTacticsResult(null);
    setImpactResult(null);
    setResult(null);
    setPreviewClaims([]);
    setError(null);
    setJobId(null);
    setJobStatus(null);
    setJobProgress(0);
    
    if (onAnalysisEnd) onAnalysisEnd();
  };
  
  // Progress bar component
  const ProgressBar = ({ progress }) => {
    return (
      <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
        <div 
          className="bg-blue-600 h-2.5 rounded-full transition-all duration-500 ease-in-out"
          style={{ width: `${progress}%` }}
        ></div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Workflow buttons at the top if provided */}
      {workflowButtons && (
        <div className="mb-4">
          {workflowButtons}
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <Cpu className="mr-2 h-5 w-5 text-indigo-600" />
          Step-by-Step Article Analysis with Claude AI
        </h3>

        {error && (
          <div className="p-4 mb-4 bg-red-50 border-l-4 border-red-500 text-red-700">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 mr-2" />
              <div>
                <p className="text-sm font-medium">{error}</p>
                
                {error.includes('timed out') && (
                  <p className="text-xs mt-2">
                    The article appears to be too long or complex for Claude to analyze within the time limit. 
                    Try with a shorter article or break it into sections.
                  </p>
                )}
                
                {error.includes('HTML error page') && (
                  <p className="text-xs mt-2">
                    The analysis server encountered an error. This could be because:
                    <ul className="list-disc ml-4 mt-1">
                      <li>The CLAUDE_API_KEY is not configured on the server</li>
                      <li>The article ID may not exist in the database</li>
                      <li>There might be a server-side issue with database access</li>
                    </ul>
                    Please contact the administrator to check the server logs.
                  </p>
                )}
                
                {error.includes('invalid JSON') && (
                  <p className="text-xs mt-2">
                    The server responded with invalid data. This usually indicates a configuration issue
                    on the server or an API endpoint problem. Please check the server logs.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {currentStage === 'initial' && !stageLoading && !result && (
          <div className="bg-indigo-50 p-4 rounded-md mb-4">
            <p className="text-sm text-indigo-800 mb-3">
              Claude AI will analyze this article in simple, sequential steps:
            </p>
            <ul className="text-sm text-indigo-800 list-disc pl-5 space-y-2">
              <li>Step 1: Identify misleading claims and extract them from the text</li>
              <li>Step 2: Identify rhetorical tactics used in the article</li>
              <li>Step 3: Assess democratic impact and historical parallels</li>
              <li>Step 4: Generate final scores and analysis</li>
            </ul>
            <p className="text-sm text-indigo-800 mt-3">
              Click the button below to start step 1, and you'll proceed through each step individually.
            </p>
            <div className="text-sm text-green-700 mt-3 bg-green-50 p-2 rounded font-medium">
              <span className="font-bold">ENHANCED STEP-BY-STEP PROCESS:</span> Each step runs as a separate job for maximum reliability.
            </div>
          </div>
        )}

        {/* Progress display */}
        {(stageLoading || currentStage !== 'initial' && currentStage !== 'completed') && (
          <div className={`p-4 rounded-md mb-4 ${stageLoading ? 'bg-blue-50' : 'bg-green-50'}`}>
            <div className="flex items-center mb-2">
              {stageLoading ? (
                <Loader className="h-5 w-5 text-blue-600 animate-spin mr-2" />
              ) : (
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
              )}
              <h4 className={`font-medium ${stageLoading ? 'text-blue-800' : 'text-green-800'}`}>
                {stageLoading ? 'Processing' : 'Completed'}: {getStageDescription(currentStage)}
              </h4>
            </div>
            
            <ProgressBar progress={getProgressPercent()} />
            
            <div className={`p-2 rounded mt-2 ${stageLoading ? 'bg-blue-100' : 'bg-green-100'}`}>
              <p className={`text-xs ${stageLoading ? 'text-blue-700' : 'text-green-700'}`}>
                Current Stage: {currentStage}
              </p>
              {currentStage === 'claims' && claimsResult && (
                <p className="text-xs text-green-700">
                  Found {claimsResult.misleading_claims?.length || 0} misleading claims
                </p>
              )}
              {currentStage === 'tactics' && tacticsResult && (
                <p className="text-xs text-green-700">
                  Identified {tacticsResult.rhetoric_markers?.length || 0} rhetorical techniques
                </p>
              )}
              {currentStage === 'impact' && impactResult && (
                <p className="text-xs text-green-700">
                  Analyzed impact on {impactResult.pillars?.length || 0} democratic pillars
                </p>
              )}
              {jobId && (
                <p className="text-xs text-green-700">
                  Job ID: {jobId}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Results summary */}
        {currentStage === 'completed' && result && (
          <div className="mb-4">
            <div className="bg-green-50 p-4 rounded-md mb-4">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                <h4 className="font-medium text-green-800">
                  Analysis Complete
                </h4>
              </div>
              
              <p className="text-sm text-green-700 mt-2">
                {result.overall_assessment?.summary || "Claude has analyzed the article and identified key claims and rhetorical tactics."}
              </p>
              
              {/* Add continue button here for workflow */}
              {(workflowButtons || isWorkflow) && (
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => onAnalysisComplete && onAnalysisComplete({
                      claims: previewClaims,
                      analysisData: result
                    })}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none"
                  >
                    Continue to Review & Publish
                  </button>
                </div>
              )}
            </div>
            
            <div className="bg-gray-50 p-4 rounded-md">
              <h4 className="font-medium text-gray-800 mb-2">Analysis Results</h4>
              
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div className="bg-white p-3 rounded border border-gray-200">
                  <div className="text-xs text-gray-500">Constitutional Accuracy</div>
                  <div className="text-lg font-semibold">
                    {result.constitutional_accuracy_score || 
                     (result.overall_assessment && result.overall_assessment.constitutional_accuracy_score) || 
                     (result.structured_analysis && result.structured_analysis.overall_assessment && 
                      result.structured_analysis.overall_assessment.constitutional_accuracy_score) || 
                     "N/A"}/100
                  </div>
                </div>
                
                <div className="bg-white p-3 rounded border border-gray-200">
                  <div className="text-xs text-gray-500">Source Credibility</div>
                  <div className="text-lg font-semibold">
                    {result.source_credibility_score || 
                     (result.overall_assessment && result.overall_assessment.source_credibility_score) || 
                     (result.structured_analysis && result.structured_analysis.overall_assessment && 
                      result.structured_analysis.overall_assessment.source_credibility_score) || 
                     "N/A"}/100
                  </div>
                </div>
                
                <div className="bg-white p-3 rounded border border-gray-200">
                  <div className="text-xs text-gray-500">Rhetoric Analysis</div>
                  <div className="text-lg font-semibold">
                    {result.rhetoric_score || 
                     (result.overall_assessment && result.overall_assessment.rhetoric_analysis_score) || 
                     (result.structured_analysis && result.structured_analysis.overall_assessment && 
                      result.structured_analysis.overall_assessment.rhetoric_analysis_score) || 
                     "N/A"}/100
                  </div>
                </div>
                
                <div className="bg-white p-3 rounded border border-gray-200">
                  <div className="text-xs text-gray-500">Overall Rating</div>
                  <div className="text-lg font-semibold">
                    {result.overall_rating || 
                     (result.overall_assessment && result.overall_assessment.combined_score) || 
                     (result.structured_analysis && result.structured_analysis.overall_assessment && 
                      result.structured_analysis.overall_assessment.combined_score) || 
                     "N/A"}/100
                  </div>
                </div>
              </div>
              
              <h5 className="font-medium text-gray-700 text-sm mb-1">Claims Identified ({previewClaims.length})</h5>
              <ul className="text-sm space-y-2">
                {previewClaims.map((claim, index) => (
                  <li key={index} className="flex items-start py-1 border-t border-gray-100">
                    <div className="flex-shrink-0 mt-1">
                      <span className={`inline-block h-2 w-2 rounded-full mr-2 ${
                        claim.importance_level >= 4 ? 'bg-red-500' :
                        claim.importance_level >= 3 ? 'bg-orange-500' : 'bg-yellow-500'
                      }`}></span>
                    </div>
                    <div className="flex-1">
                      <div className="font-medium">{claim.claim_text.substring(0, 100)}{claim.claim_text.length > 100 ? '...' : ''}</div>
                      <div className="text-xs text-gray-500 flex items-center mt-1">
                        {claim.rhetorical_tactics && claim.rhetorical_tactics.length > 0 && (
                          <span className="mr-2 flex items-center text-indigo-600">
                            <MessageSquare className="h-3 w-3 mr-1" />
                            {claim.rhetorical_tactics.length} {claim.rhetorical_tactics.length === 1 ? 'tactic' : 'tactics'}
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex justify-end mt-4">
          {/* Initial start button */}
          {currentStage === 'initial' && !stageLoading && !result && (
            <div>
              <button
                id="claude-analyze-step1-button"
                type="button"
                onClick={processCurrentStage}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
                disabled={stageLoading}
              >
                <Cpu className="mr-2 h-4 w-4" />
                Step 1: Analyze Claims
              </button>
            </div>
          )}
          
          {/* Claims stage completed - proceed to tactics */}
          {currentStage === 'claims' && !stageLoading && claimsResult && (
            <button
              type="button"
              onClick={processCurrentStage}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
              disabled={stageLoading}
            >
              <ArrowRight className="mr-2 h-4 w-4" />
              Step 2: Analyze Tactics
            </button>
          )}
          
          {/* Tactics stage completed - proceed to impact */}
          {currentStage === 'tactics' && !stageLoading && tacticsResult && (
            <button
              type="button"
              onClick={processCurrentStage}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
              disabled={stageLoading}
            >
              <ArrowRight className="mr-2 h-4 w-4" />
              Step 3: Analyze Democratic Impact
            </button>
          )}
          
          {/* Impact stage completed - proceed to finalize */}
          {currentStage === 'impact' && !stageLoading && impactResult && (
            <button
              type="button"
              onClick={processCurrentStage}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
              disabled={stageLoading}
            >
              <ArrowRight className="mr-2 h-4 w-4" />
              Step 4: Generate Final Analysis
            </button>
          )}
          
          {/* Loading state - show spinner */}
          {stageLoading && (
            <button
              type="button"
              disabled
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded shadow-sm text-gray-400 bg-gray-100"
            >
              <Loader className="animate-spin mr-2 h-4 w-4" />
              Processing...
            </button>
          )}
          
          {/* Reset button for completed analysis */}
          {currentStage === 'completed' && result && (
            <div className="flex space-x-4">
              {/* Continue button for workflow mode */}
              {(isWorkflow || workflowButtons) && (
                <button
                  type="button"
                  onClick={() => onAnalysisComplete && onAnalysisComplete({
                    claims: previewClaims,
                    analysisData: result
                  })}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none"
                >
                  Continue to Review & Publish
                </button>
              )}
              
              {/* Reset button */}
              <button
                type="button"
                onClick={resetAnalysis}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
              >
                <XCircle className="mr-2 h-4 w-4" />
                Reset Analysis
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StepByStepArticleAnalyzer;